import {
	Card,
	Form,
	Input,
	Button,
	Select,
	Row,
	Col,
	Typography,
	message,
	notification,
	Spin,
	Space,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	requestGetServiceGroupById,
	requestUpdateServiceGroup,
} from "../../services/admin.service";
import { PiArrowLeftDuotone, PiEyeDuotone } from "react-icons/pi";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Constants based on schema
const STATUS_OPTIONS = [
	{ label: "Active", value: "ACTIVE" },
	{ label: "Inactive", value: "INACTIVE" },
];

function EditServiceGroupPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [initialLoading, setInitialLoading] = useState(true);
	const [serviceGroupData, setServiceGroupData] = useState(null);

	useEffect(() => {
		document.title = "Cloudiva Console | Edit Service Group";
		if (id) {
			fetchServiceGroupData();
		}
	}, [id]);

	const fetchServiceGroupData = () => {
		setInitialLoading(true);
		requestGetServiceGroupById(id)
			.then((response) => {
				const serviceGroup = response.data.data;
				setServiceGroupData(serviceGroup);

				// Populate form with existing data
				form.setFieldsValue({
					code: serviceGroup.code,
					name: serviceGroup.name,
					description: serviceGroup.description,
					status: serviceGroup.status,
					metadata: serviceGroup.metadata ? JSON.stringify(serviceGroup.metadata, null, 2) : "",
				});
			})
			.catch(() => {
				notification.error({
					message: "Error",
					description: "Failed to fetch service group data",
				});
				navigate("/service-groups");
			})
			.finally(() => {
				setInitialLoading(false);
			});
	};

	const handleSubmit = () => {
		form
			.validateFields()
			.then((values) => {
				setLoading(true);

				// Process the form data to match the schema
				const serviceGroupData = {
					code: values.code,
					name: values.name,
					description: values.description || "",
					status: values.status || "ACTIVE",
					metadata: values.metadata ? JSON.parse(values.metadata) : {},
				};

				requestUpdateServiceGroup(id, serviceGroupData)
					.then(() => {
						message.success("Service group updated successfully!");
						navigate("/service-groups");
					})
					.catch((error) => {
						console.error("Error updating service group:", error);
						if (error.response?.data?.message) {
							notification.error({
								message: "Error",
								description: error.response.data.message,
							});
						} else {
							notification.error({
								message: "Error",
								description: "Failed to update service group. Please try again.",
							});
						}
					})
					.finally(() => {
						setLoading(false);
					});
			})
			.catch((error) => {
				console.error("Validation failed:", error);
			});
	};

	const validateJSON = (_, value) => {
		if (!value) return Promise.resolve();
		try {
			JSON.parse(value);
			return Promise.resolve();
		} catch (error) {
			return Promise.reject(new Error("Please enter valid JSON"));
		}
	};

	if (initialLoading) {
		return (
			<div
				style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button
						icon={<PiArrowLeftDuotone />}
						onClick={() => navigate("/service-groups")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						{serviceGroupData ? `Edit Service Group: ${serviceGroupData.name}` : "Loading..."}
					</Title>
					<Button
						type="link"
						size="small"
						icon={<PiEyeDuotone />}
						onClick={() => navigate(`/service-groups/${serviceGroupData?._id}`)}>
						View Service Group
					</Button>
				</Space>
			</div>
			<div className="page-content-wrapper">
				<Card
					title="Edit Service Group"
					extra={
						<div>
							<Button onClick={() => navigate("/service-groups")} style={{ marginRight: 8 }}>
								Cancel
							</Button>
							<Button type="primary" loading={loading} onClick={handleSubmit}>
								Update Service Group
							</Button>
						</div>
					}>
					<Form form={form} layout="vertical">
						<Row gutter={16}>
							<Col span={12}>
								<Form.Item
									label="Service Group Code"
									name="code"
									rules={[
										{ required: true, message: "Please enter service group code" },
										{
											pattern: /^[A-Z0-9_-]+$/,
											message:
												"Code must contain only uppercase letters, numbers, underscores, and hyphens",
										},
									]}>
									<Input placeholder="Enter service group code" />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									label="Service Group Name"
									name="name"
									rules={[{ required: true, message: "Please enter service group name" }]}>
									<Input placeholder="Enter service group name" />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={24}>
								<Form.Item label="Description" name="description">
									<TextArea rows={4} placeholder="Enter service group description" />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									label="Metadata (JSON)"
									name="metadata"
									rules={[{ validator: validateJSON }]}>
									<TextArea rows={6} placeholder="Enter metadata as JSON (optional)" />
								</Form.Item>
							</Col>
						</Row>
					</Form>
				</Card>
			</div>
		</div>
	);
}

export default EditServiceGroupPage;
